import React, { createContext, useContext, useEffect, useState, useRef } from "react";

const ThemeProviderContext = createContext({
  theme: "light",
  setTheme: () => null,
});

export function ThemeProvider({ children, defaultTheme = "light", storageKey = "theme" }) {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if user has a saved theme preference
    const savedTheme = localStorage.getItem(storageKey);
    // Always default to light mode if no saved preference
    return savedTheme === 'dark';
  });
  
  // Use ref to track theme transitions to avoid multiple simultaneous changes
  const transitioning = useRef(false);

  // Apply theme when component mounts or theme changes
  useEffect(() => {
    // Prevent multiple rapid transitions
    if (transitioning.current) return;
    
    transitioning.current = true;
    
    if (isDarkMode) {
      document.body.classList.add('dark-theme');
      localStorage.setItem(storageKey, 'dark');
    } else {
      document.body.classList.remove('dark-theme');
      localStorage.setItem(storageKey, 'light');
    }
    
    // Also update the root element class for tailwind compatibility
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(isDarkMode ? "dark" : "light");
    
    // Reset transition flag after CSS transition completes
    const transitionDuration = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--transition-speed')
    ) * 1000 || 500; // Default to 500ms if can't get value
    
    setTimeout(() => {
      transitioning.current = false;
    }, transitionDuration);
  }, [isDarkMode, storageKey]);

  // Initialize theme on mount
  useEffect(() => {
    // Apply theme based on initial state
    if (isDarkMode) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if the user hasn't set their own preference
      if (!localStorage.getItem(storageKey)) {
        // Always use light mode regardless of system preference
        setIsDarkMode(false);
      }
    };

    // Add listener and cleanup
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isDarkMode, storageKey]);

  const value = {
    theme: isDarkMode ? "dark" : "light",
    setTheme: (newTheme) => {
      // Don't allow theme changes during transition
      if (transitioning.current) return;
      
      if (typeof newTheme === 'string') {
        // Handle string input for backward compatibility
        setIsDarkMode(newTheme === "dark");
      } else {
        // Handle direct boolean setting
        setIsDarkMode(newTheme);
      }
    },
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}; 